// Modelo da Planilha de Fechamento (issue #92): a Fatura em matriz, montada
// UMA vez — colunas agrupadas por Categoria, as linhas (OS, Compensação,
// Relavagem em cinza) e o rodapé já calculado. A tela, o CSV e o PDF/JPEG
// (`_planilha.ts`) só desenham este modelo: antes cada um refazia o rodapé, a
// porcentagem e os preços, e o CSV nem tinha a linha de %.
// Puro (sem DOM).

import { formatData } from "../../domain/datas.ts";
import { planilhaFechamento, type LinhaPlanilha } from "../../domain/faturamento.ts";
import type { Id } from "../../domain/ids.ts";
import type { Snapshot } from "../../domain/snapshot.ts";

export interface GrupoCategoria {
  nome: string;
  /** Quantas colunas de Tipo este grupo cobre. */
  span: number;
}

export interface LinhaModelo {
  /** "dd/mm/aaaa". */
  data: string;
  /** "1044", "(Relav) 1044" ou "(Comp.) 1044". */
  os: string;
  /** Fora do fluxo normal de OS (Relavagem, Compensação): fundo cinza. */
  cinza: boolean;
  /** Peças que voltaram do Backlog nesta OS — a marca "+N ant." só na tela. */
  deRetidas: number;
  /** Na ordem de `colunas`. */
  quantidades: number[];
  total: number;
}

export interface ModeloPlanilha {
  grupos: GrupoCategoria[];
  colunas: { tipoId: Id; nome: string }[];
  linhas: LinhaModelo[];
  /** Há OS de Relavagem no período (mesmo que fora do exportado). */
  temRelavagem: boolean;
  totalPecas: { porTipo: number[]; total: number };
  percentuais: { porTipo: string[]; total: string };
  /** Os preços usados de cada Tipo, em centavos (ver `precosUsados`). */
  unitarios: number[][];
  subtotal: { porTipo: number[]; total: number };
  ajustes: { descricao: string; centavos: number }[];
  totalGeral: number;
}

/**
 * O "Valor unitário" de uma peça: os preços usados quando há mais de um (em
 * ordem de data), senão o único. Nunca a média — ela não é um preço da tabela
 * e não fecha na conferência (quantidade × unitário ≠ subtotal). Fatura
 * emitida antes de os preços serem congelados não os tem: aí vale a média.
 * Também usado no detalhe da Fatura.
 */
export function precosUsados(precos: readonly number[], media: number): number[] {
  return precos.length > 0 ? [...precos] : [media];
}

/** "(Comp.) nº" — também na lista de compensações do detalhe da Fatura. */
export function rotuloCompensacao(os: number): string {
  return `(Comp.) ${os}`;
}

/** Rótulo da OS numa linha: Relavagem (ADR-0015) e Compensação (ADR-0031) levam a marca. */
function rotuloOS(l: LinhaPlanilha): string {
  switch (l.origem) {
    case "relavagem":
      return `(Relav) ${l.os}`;
    case "compensacao":
      return rotuloCompensacao(l.os);
    case "lote":
      return String(l.os);
  }
}

/** Ordem das peças por Categoria e, dentro dela, por nome — a da Planilha e do detalhe da Fatura. */
export function ordemPorCategoria(snap: Snapshot): (a: Id, b: Id) => number {
  const nome = new Map(snap.tiposPeca.map((t) => [t.id, t.nome]));
  const categoria = categoriaDoTipo(snap);
  return (a, b) =>
    categoria(a).localeCompare(categoria(b), "pt-BR") || (nome.get(a) ?? "").localeCompare(nome.get(b) ?? "", "pt-BR");
}

/** O nome da Categoria de um Tipo de Peça ("—" sem Categoria). */
export function categoriaDoTipo(snap: Snapshot): (tipoId: Id) => string {
  const catDoTipo = new Map(snap.tiposPeca.map((t) => [t.id, t.categoriaId]));
  const catNome = new Map(snap.categorias.map((c) => [c.id, c.nome]));
  return (tipoId) => catNome.get(catDoTipo.get(tipoId) ?? "") ?? "—";
}

export function modeloPlanilha(snap: Snapshot, faturaId: Id, opcoes: { incluirRelavagem: boolean }): ModeloPlanilha {
  const d = planilhaFechamento(snap, faturaId, ordemPorCategoria(snap));
  const nome = new Map(snap.tiposPeca.map((t) => [t.id, t.nome]));
  const categoria = categoriaDoTipo(snap);

  const grupos: GrupoCategoria[] = [];
  for (const id of d.tipos) {
    const n = categoria(id);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.nome === n) ultimo.span += 1;
    else grupos.push({ nome: n, span: 1 });
  }
  const pct = (n: number): string => (d.totalPecas === 0 ? "—" : `${Math.round((n / d.totalPecas) * 100)}%`);
  const porTipo = <T>(f: (id: Id) => T): T[] => d.tipos.map(f);

  return {
    grupos,
    colunas: d.tipos.map((tipoId) => ({ tipoId, nome: nome.get(tipoId) ?? tipoId })),
    linhas: d.linhas
      .filter((l) => opcoes.incluirRelavagem || l.origem !== "relavagem")
      .map((l) => ({
        data: formatData(l.data),
        os: rotuloOS(l),
        cinza: l.origem !== "lote",
        deRetidas: l.deRetidas,
        quantidades: porTipo((id) => l.porTipo.get(id) ?? 0),
        total: l.total,
      })),
    temRelavagem: d.linhas.some((l) => l.origem === "relavagem"),
    totalPecas: { porTipo: porTipo((id) => d.totalPorTipo.get(id) ?? 0), total: d.totalPecas },
    percentuais: { porTipo: porTipo((id) => pct(d.totalPorTipo.get(id) ?? 0)), total: d.totalPecas === 0 ? "—" : "100%" },
    unitarios: porTipo((id) => precosUsados(d.precosPorTipo.get(id) ?? [], d.unitarioPorTipo.get(id) ?? 0)),
    subtotal: { porTipo: porTipo((id) => d.subtotalPorTipo.get(id) ?? 0), total: d.subtotalPecasCentavos },
    ajustes: d.ajustes.map((a) => ({ descricao: a.descricao, centavos: a.valorCentavos })),
    totalGeral: d.totalCentavos,
  };
}
